import {
  BACKUP_KIND_LABELS,
  type BackupRun,
  formatBytes,
  type MetricSample,
  type OpsStatus,
  RATE_LIMIT_SCOPE_LABELS,
  type RateLimitStats,
} from "@cva/shared";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { AlertTriangle, Info } from "lucide-react";
import { type ReactNode, useState } from "react";
import { Badge, Card, CardHeader, Progress, Skeleton } from "../../components/ui/card";
import { orpc } from "../../lib/api";
import { cn, formatDateTime, formatRelative } from "../../lib/utils";
import { AdminPageHeader, LEVEL_STYLE, StatusDot } from "./layout";

// --- Sparkline: one series, one hue, 2 px line, hover crosshair ----------------------

function Sparkline({
  samples,
  value,
  format,
  max,
}: {
  samples: MetricSample[];
  value: (sample: MetricSample) => number | null;
  format: (value: number) => string;
  /** Fixed upper bound (e.g. 100 %); default: data maximum. */
  max?: number;
}) {
  const [hover, setHover] = useState<number | null>(null);
  const points = samples
    .map((sample) => ({ ts: sample.ts, v: value(sample) }))
    .filter(
      (point): point is { ts: Date; v: number } => point.v !== null && Number.isFinite(point.v),
    );
  if (points.length < 2) {
    return <p className="flex h-16 items-center text-xs text-zinc-400">Noch zu wenig Messwerte</p>;
  }
  const width = 280;
  const height = 64;
  const top = max ?? Math.max(...points.map((p) => p.v), 1e-9);
  const first = points[0]!.ts.getTime();
  const span = Math.max(1, points[points.length - 1]!.ts.getTime() - first);
  const x = (ts: Date) => ((ts.getTime() - first) / span) * width;
  const y = (v: number) => height - 4 - (Math.min(v, top) / top) * (height - 8);
  const line = points
    .map((p, i) => `${i ? "L" : "M"}${x(p.ts).toFixed(1)} ${y(p.v).toFixed(1)}`)
    .join("");
  const active = hover === null ? null : points[hover];
  return (
    <div className="relative">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="h-16 w-full overflow-visible"
        preserveAspectRatio="none"
        role="img"
        aria-label={`Verlauf, zuletzt ${format(points[points.length - 1]!.v)}`}
        onMouseLeave={() => setHover(null)}
        onMouseMove={(event) => {
          const rect = event.currentTarget.getBoundingClientRect();
          const ratio = (event.clientX - rect.left) / rect.width;
          const target = first + ratio * span;
          let best = 0;
          points.forEach((p, i) => {
            if (Math.abs(p.ts.getTime() - target) < Math.abs(points[best]!.ts.getTime() - target))
              best = i;
          });
          setHover(best);
        }}
      >
        <line x1="0" x2={width} y1={height - 4} y2={height - 4} stroke="#ddd8e8" strokeWidth="1" />
        <path d={`${line}L${width} ${height - 4}L0 ${height - 4}Z`} fill="#2457eb" opacity="0.08" />
        <path
          d={line}
          fill="none"
          stroke="#2457eb"
          strokeWidth="2"
          vectorEffect="non-scaling-stroke"
          strokeLinejoin="round"
        />
        {active ? (
          <>
            <line
              x1={x(active.ts)}
              x2={x(active.ts)}
              y1="0"
              y2={height}
              stroke="#938ba5"
              strokeWidth="1"
              vectorEffect="non-scaling-stroke"
            />
            <circle
              cx={x(active.ts)}
              cy={y(active.v)}
              r="4"
              fill="#2457eb"
              stroke="#fff"
              strokeWidth="2"
              vectorEffect="non-scaling-stroke"
            />
          </>
        ) : null}
      </svg>
      {active ? (
        <div className="pointer-events-none absolute -top-8 right-0 rounded-md bg-zinc-900 px-2 py-1 text-xs whitespace-nowrap text-white shadow">
          {format(active.v)} ·{" "}
          {active.ts.toLocaleString("de-DE", {
            day: "2-digit",
            month: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
          })}
        </div>
      ) : null}
    </div>
  );
}

function ChartCard({
  title,
  current,
  children,
}: {
  title: string;
  current: ReactNode;
  children: ReactNode;
}) {
  return (
    <Card className="p-4">
      <div className="mb-2 flex items-baseline justify-between gap-2">
        <h3 className="text-sm font-medium text-zinc-600">{title}</h3>
        <span className="text-sm font-semibold text-zinc-900 tabular-nums">{current}</span>
      </div>
      {children}
    </Card>
  );
}

function Stat({ label, value, hint }: { label: string; value: ReactNode; hint?: ReactNode }) {
  return (
    <Card className="p-4">
      <p className="text-xs font-medium text-zinc-700">{label}</p>
      <p className="mt-1 text-2xl font-semibold text-zinc-900 tabular-nums">{value}</p>
      {hint ? <p className="mt-0.5 text-xs text-zinc-500">{hint}</p> : null}
    </Card>
  );
}

function BackupLine({
  label,
  run,
  missing,
}: {
  label: string;
  run: BackupRun | null;
  missing: string;
}) {
  return (
    <li className="flex items-start gap-3 px-5 py-3">
      <StatusDot
        level={run ? (run.status === "ok" ? "ok" : "critical") : "unknown"}
        className="mt-1.5"
      />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-zinc-900">{label}</p>
        <p className="truncate text-xs text-zinc-500">
          {run
            ? `${run.status === "ok" ? "Erfolgreich" : "Fehlgeschlagen"} · ${formatRelative(run.finishedAt)}${run.sizeBytes ? ` · ${formatBytes(run.sizeBytes)}` : ""}${run.message ? ` · ${run.message}` : ""}`
            : missing}
        </p>
      </div>
    </li>
  );
}

function Hints({ status }: { status: OpsStatus }) {
  const hints: ReactNode[] = [];
  const n = status.notifications;
  if (!n.alertsEnabled) hints.push("Warnungen sind abgeschaltet (ALERTS_ENABLED=false).");
  if (!n.smtp && !n.webhook) {
    hints.push(
      <>
        Warnungen gehen derzeit an <strong>keinen</strong> Kanal: Es ist weder SMTP noch
        ALERT_WEBHOOK_URL eingerichtet. Sie erscheinen nur hier.
      </>,
    );
  } else if (!n.smtp) {
    hints.push("Kein SMTP eingerichtet: Warnungen kommen nur per Webhook an, nicht per E-Mail.");
  }
  if (!n.heartbeat) {
    hints.push(
      "Kein Dead-Man's-Switch (HEARTBEAT_URL): Fällt der ganze Server aus, meldet das nur der stündliche Uptime-Check von GitHub.",
    );
  }
  if (status.backups.expected && !status.backups.lastOffsite) {
    hints.push(
      "Kein Off-Site-Backup eingerichtet: Die Sicherungen liegen nur auf demselben Server.",
    );
  }
  if (hints.length === 0) return null;
  return (
    <Card className="border-amber-200 bg-amber-50">
      <ul className="space-y-2 p-4 text-sm text-amber-900">
        {hints.map((hint, index) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: static list
          <li key={index} className="flex gap-2">
            <Info className="mt-0.5 size-4 shrink-0" />
            <span>{hint}</span>
          </li>
        ))}
      </ul>
    </Card>
  );
}

/** Requests refused by the rate limits, all API processes together (docs/13-betrieb.md). */
function RateLimitCard({ stats }: { stats: RateLimitStats }) {
  return (
    <Card>
      <CardHeader
        title="Rate-Limits"
        description={
          stats.enabled
            ? `${stats.lastHour} abgewiesene Anfragen in der letzten Stunde, ${stats.last5m} in den letzten 5 Minuten`
            : "Ausgeschaltet (RATE_LIMIT_ENABLED)"
        }
      />
      {stats.byScope.length > 0 ? (
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-zinc-500">
            <tr>
              <th className="px-5 py-2 font-medium">Limit</th>
              <th className="px-3 py-2 text-right font-medium">5 min</th>
              <th className="px-5 py-2 text-right font-medium">1 h</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100 tabular-nums">
            {stats.byScope.map((entry) => (
              <tr key={entry.scope}>
                <td className="px-5 py-2.5">
                  {RATE_LIMIT_SCOPE_LABELS[entry.scope] ?? entry.scope}
                </td>
                <td className="px-3 py-2.5 text-right">{entry.last5m}</td>
                <td className="px-5 py-2.5 text-right">{entry.lastHour}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : stats.enabled ? (
        <p className="px-5 py-3 text-sm text-zinc-500">Keine abgewiesenen Anfragen.</p>
      ) : null}
    </Card>
  );
}

export function AdminOverviewPage() {
  const status = useQuery({ ...orpc.admin.overview.queryOptions(), refetchInterval: 30_000 });
  const metrics = useQuery({
    ...orpc.admin.metrics.queryOptions({ input: { hours: 24 } }),
    refetchInterval: 5 * 60_000,
  });

  if (!status.data) {
    return (
      <>
        <AdminPageHeader title="Übersicht" />
        <Skeleton className="h-96" />
      </>
    );
  }
  const s = status.data;
  const samples = metrics.data ?? [];
  const worker = s.processes.find((p) => p.role === "worker");
  const api = s.processes.find((p) => p.role === "api");
  const diskTone =
    s.host.diskPercent >= 90 ? "danger" : s.host.diskPercent >= 80 ? "warning" : "brand";

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="Übersicht"
        description={`${s.env} · Version ${s.version.slice(0, 12)} · aktualisiert ${s.generatedAt.toLocaleTimeString("de-DE")}`}
        actions={
          <Badge tone={LEVEL_STYLE[s.overall].tone} className="px-3 py-1 text-sm">
            <StatusDot level={s.overall} className="mr-2" />
            {s.overall === "ok" ? "Alles in Ordnung" : LEVEL_STYLE[s.overall].label}
          </Badge>
        }
      />

      {s.alerts.length > 0 ? (
        <Card
          className={cn(
            s.alerts.some((a) => a.severity === "critical") ? "border-red-200" : "border-amber-200",
          )}
        >
          <CardHeader
            title={`${s.alerts.length} offene Warnung${s.alerts.length === 1 ? "" : "en"}`}
            actions={
              <Link to="/admin/alerts" className="text-sm font-medium text-brand-600">
                Alle ansehen
              </Link>
            }
          />
          <ul className="divide-y divide-zinc-100">
            {s.alerts.map((alert) => (
              <li key={alert.id} className="flex items-start gap-3 px-5 py-3">
                <AlertTriangle
                  className={cn(
                    "mt-0.5 size-4 shrink-0",
                    alert.severity === "critical" ? "text-red-600" : "text-amber-500",
                  )}
                />
                <div className="min-w-0">
                  <p className="text-sm font-medium text-zinc-900">{alert.title}</p>
                  <p className="text-sm text-zinc-600">{alert.message}</p>
                  <p className="text-xs text-zinc-400">seit {formatRelative(alert.openedAt)}</p>
                </div>
              </li>
            ))}
          </ul>
        </Card>
      ) : null}

      <Hints status={s} />

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
        {s.services.map((service) => (
          <Card key={service.id} className="p-4">
            <div className="flex items-center gap-2">
              <StatusDot level={service.status} />
              <p className="text-sm font-semibold text-zinc-900">{service.label}</p>
            </div>
            <p className="mt-1 text-xs text-zinc-500">{service.detail}</p>
            {service.latencyMs !== null ? (
              <p className="mt-1 text-xs text-zinc-400 tabular-nums">{service.latencyMs} ms</p>
            ) : null}
          </Card>
        ))}
      </div>

      <div className="grid gap-3 sm:grid-cols-3 xl:grid-cols-6">
        <Stat label="Konten" value={s.app.users} hint={`+${s.app.usersNew7d} in 7 Tagen`} />
        <Stat label="Aktiv (7 T.)" value={s.app.activeUsers7d} />
        <Stat label="Organisationen" value={s.app.orgs} />
        <Stat label="Dateien" value={s.app.documents} />
        <Stat label="Medien" value={s.app.assets} />
        <Stat label="Speicher" value={formatBytes(s.app.storageBytes)} />
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <ChartCard title="Festplatte" current={`${s.host.diskPercent.toLocaleString("de-DE")} %`}>
          <Progress value={s.host.diskPercent} tone={diskTone} className="mb-3" />
          <Sparkline
            samples={samples}
            value={(m) => m.diskPercent}
            format={(v) => `${v.toFixed(1)} %`}
            max={100}
          />
          <p className="mt-1 text-xs text-zinc-500">
            {formatBytes(s.host.diskTotalBytes - s.host.diskUsedBytes)} frei von{" "}
            {formatBytes(s.host.diskTotalBytes)}
          </p>
        </ChartCard>
        <ChartCard
          title="Worker-Speicher"
          current={
            worker
              ? `${formatBytes(worker.memoryUsedBytes ?? worker.rssBytes)}${worker.memoryLimitBytes ? ` / ${formatBytes(worker.memoryLimitBytes)}` : ""}`
              : "–"
          }
        >
          <Sparkline
            samples={samples}
            value={(m) => m.workerMemoryBytes}
            format={(v) => formatBytes(v)}
            max={worker?.memoryLimitBytes ?? undefined}
          />
          <p className="mt-1 text-xs text-zinc-500">
            Server: {formatBytes(s.host.memAvailableBytes)} von {formatBytes(s.host.memTotalBytes)}{" "}
            verfügbar · Last {s.host.load1.toLocaleString("de-DE")} ({s.host.cpus} Kerne)
          </p>
        </ChartCard>
        <ChartCard title="Wartende Jobs" current={s.queues.reduce((sum, q) => sum + q.waiting, 0)}>
          <Sparkline
            samples={samples}
            value={(m) => m.queueWaiting}
            format={(v) => `${Math.round(v)} Jobs`}
          />
          <p className="mt-1 text-xs text-zinc-500">
            {s.queues.reduce((sum, q) => sum + q.failedLastHour, 0)} fehlgeschlagen in der letzten
            Stunde
          </p>
        </ChartCard>
        <ChartCard
          title="API-Antwortzeit (p95)"
          current={api?.requests?.p95Ms != null ? `${Math.round(api.requests.p95Ms)} ms` : "–"}
        >
          <Sparkline
            samples={samples}
            value={(m) => m.p95Ms}
            format={(v) => `${Math.round(v)} ms`}
          />
          <p className="mt-1 text-xs text-zinc-500">
            {api?.requests?.total ?? 0} Anfragen in 5 min · Event-Loop {api?.eventLoopLagMs ?? "–"}{" "}
            ms
          </p>
        </ChartCard>
        <ChartCard title="Serverfehler (5xx)" current={api?.requests?.errors5xx ?? 0}>
          <Sparkline
            samples={samples}
            value={(m) => m.errors5xx}
            format={(v) => `${Math.round(v)} in 5 min`}
          />
          <p className="mt-1 text-xs text-zinc-500">je 5-Minuten-Fenster</p>
        </ChartCard>
        <Card>
          <CardHeader
            title="Backups"
            actions={
              <Link to="/admin/backups" className="text-sm font-medium text-brand-600">
                Details
              </Link>
            }
          />
          <ul className="divide-y divide-zinc-100">
            <BackupLine
              label={BACKUP_KIND_LABELS.daily}
              run={s.backups.lastDaily}
              missing={s.backups.expected ? "Noch keins" : "Nicht in dieser Umgebung"}
            />
            <BackupLine
              label={BACKUP_KIND_LABELS.verify}
              run={s.backups.lastVerify}
              missing="Noch keiner"
            />
            <BackupLine
              label={BACKUP_KIND_LABELS.offsite}
              run={s.backups.lastOffsite}
              missing="Nicht eingerichtet"
            />
          </ul>
        </Card>
      </div>

      <Card>
        <CardHeader title="Warteschlangen" />
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-zinc-500">
            <tr>
              <th className="px-5 py-2 font-medium">Warteschlange</th>
              <th className="px-3 py-2 text-right font-medium">Wartend</th>
              <th className="px-3 py-2 text-right font-medium">Aktiv</th>
              <th className="px-3 py-2 text-right font-medium">Geplant</th>
              <th className="px-3 py-2 text-right font-medium">Fehler (1 h)</th>
              <th className="px-5 py-2 text-right font-medium">Ältester wartet</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100 tabular-nums">
            {s.queues.map((queue) => (
              <tr key={queue.name}>
                <td className="px-5 py-2.5">{queue.label}</td>
                <td className="px-3 py-2.5 text-right">{queue.waiting}</td>
                <td className="px-3 py-2.5 text-right">{queue.active}</td>
                <td className="px-3 py-2.5 text-right">{queue.delayed}</td>
                <td
                  className={cn(
                    "px-3 py-2.5 text-right",
                    queue.failedLastHour > 0 && "text-red-600",
                  )}
                >
                  {queue.failedLastHour}
                </td>
                <td className="px-5 py-2.5 text-right">
                  {queue.oldestWaitingSeconds === null
                    ? "–"
                    : `${Math.round(queue.oldestWaitingSeconds / 60)} min`}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <RateLimitCard stats={s.rateLimits} />

      <Card>
        <CardHeader title="Prozesse" />
        <ul className="divide-y divide-zinc-100">
          {s.processes.map((proc) => (
            <li key={proc.role} className="grid gap-1 px-5 py-3 text-sm sm:grid-cols-4">
              <span className="font-medium">{proc.role === "api" ? "API" : "Worker"}</span>
              <span className="text-zinc-600">läuft seit {formatDateTime(proc.startedAt)}</span>
              <span className="text-zinc-600 tabular-nums">
                {formatBytes(proc.memoryUsedBytes ?? proc.rssBytes)}
                {proc.memoryLimitBytes ? ` von ${formatBytes(proc.memoryLimitBytes)}` : ""}
              </span>
              <span className="text-zinc-500">Rückmeldung {formatRelative(proc.lastSeen)}</span>
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}
